import { ImsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import FileCheck02Icon from "@redotech/redo-icons/file-check-02.svg";
import FileCheck02FilledIcon from "@redotech/redo-icons/file-check-02_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { useIntelligentCycleCountsFlag } from "@redotech/redo-merchant-app-common/intelligent-cycle-counts-flag-provider";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import BarChart10FilledIcon from "@redotech/redo-web/icon-old/bar-chart-10-filled.svg";
import BarChart10Icon from "@redotech/redo-web/icon-old/bar-chart-10.svg";
import { memo } from "react";
import { useMatch } from "react-router-dom";
import { useCustomAnalyticsReportsFeatureFlag } from "../../../../analytics/reports/custom/hooks/use-custom-analytics-reports-feature-flag";
import { InventoryManagementSuiteStatusesContext } from "../../../../redo-suite/inventory-cloud";

export const InventoryAnalyticsItem = memo(function InventoryAnalyticsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );

  const canAccessInventory = useHasPermission(ImsPermissions.ACCESS_INVENTORY);
  const canViewReports = useHasPermission(ImsPermissions.VIEW_REPORTS);
  const canAccessCycleCounts = useHasPermission(
    ImsPermissions.ACCESS_CYCLE_COUNTS,
  );
  const customReportsEnabled =
    useCustomAnalyticsReportsFeatureFlag().data ?? false;
  const { enabled: intelligentCycleCountsEnabled } =
    useIntelligentCycleCountsFlag();

  const showCycleCounts =
    imsEnabled && canAccessCycleCounts && intelligentCycleCountsEnabled;
  const showAdjustmentReports = imsEnabled && canAccessInventory;
  const showCustomReports =
    imsEnabled && canViewReports && customReportsEnabled;
  const showAnalytics =
    showCycleCounts || showAdjustmentReports || showCustomReports;

  const analyticsRoute = `${routes.inventoryManagementBaseUrl}/analytics`;
  const analyticsChildSelected = useMatch(`${analyticsRoute}/*`);
  const adjustmentReportSelected = useMatch(
    `${routes.inventoryManagementBaseUrl}/adjustment-report`,
  );
  const anyChildSelected =
    !!analyticsChildSelected || !!adjustmentReportSelected;

  if (!showAnalytics) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={anyChildSelected}
      FilledIcon={BarChart10FilledIcon}
      friendlyName="Analytics"
      id="inventory-analytics"
      isFolder
      UnfilledIcon={BarChart10Icon}
    >
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.inventoryManagementBaseUrl}/analytics/cycle-counts`,
        }}
        friendlyName="Cycle Counts"
        hidden={!showCycleCounts}
        id="inventory-analytics-cycle-counts"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.inventoryManagementBaseUrl}/adjustment-report`,
        }}
        FilledIcon={FileCheck02FilledIcon}
        friendlyName="Adjustment reports"
        hidden={!showAdjustmentReports}
        id="inventory-adjustment-reports"
        UnfilledIcon={FileCheck02Icon}
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.inventoryAnalyticsReportsHref }}
        beta
        friendlyName="Custom Analytics"
        hidden={!showCustomReports}
        id="inventory-analytics-custom-reports"
      />
    </RedoSecondaryNavItem>
  );
});
